/**
 * @file schemas/m2.ts
 * @description M2 稽核執行前後端共用 Zod Schema。
 *
 * 涵蓋：
 * - 稽核任務（confirmAuditTask, checkIn, checkOut）
 * - 稽核記錄
 * - 稽核發現事項（NC）
 * - 採樣
 * - 實驗室報告
 * - 有機資材審查
 * - 風險評估
 * - 報告樣式模板
 * - AI 報告生成
 */
import { z } from 'zod';
import { AUDIT_TASK_PHASES, AUDIT_TASK_STATUSES, AUDIT_TASK_TYPES, VERIFICATION_SCHEMES, } from '../enums';
// ─── 稽核任務 ──────────────────────────────────────────────────────────────────
/**
 * 派案確認稽核小組成員角色（序63.1）。
 * 對應 case_audit_team.role 承載值；'auditor'＝稽核員、'observer'＝觀察員、
 * 'assistant'＝驗證助理。
 * 2026-09-13 回報序18 新增：'trainee'＝實習稽核員、'expert'＝技術專家
 * （case_audit_team.role 為 varchar 存中文標籤，新增角色無需 DB 遷移）。
 * 2026-09-15 回報序26 新增：'lead'＝主導稽核員，並依客戶指定順序排列：
 * 主導稽核員、稽核員、實習稽核員、技術專家、觀察員、驗證助理。
 */
export const DISPATCH_TEAM_MEMBER_ROLES = [
    'lead',
    'auditor',
    'trainee',
    'expert',
    'observer',
    'assistant',
];
/** 派案確認角色 → 顯示名稱（序63.1 / 序18 / 序26） */
export const DISPATCH_TEAM_MEMBER_ROLE_LABELS = {
    lead: '主導稽核員',
    auditor: '稽核員',
    trainee: '實習稽核員',
    expert: '技術專家',
    observer: '觀察員',
    assistant: '驗證助理',
};
/**
 * 派案確認角色 → case_audit_team.role 實際落庫標籤。
 * ⚠️ 主導稽核員沿用既有落庫值「稽核組長」：派案 bootstrap 以 role==='稽核組長' 判定
 * isTeamLead，若改存「主導稽核員」會導致重新派遣時找不到組長任務。其餘角色落庫值＝顯示名稱。
 */
export function dispatchTeamRoleStorageLabel(role) {
    return role === 'lead' ? '稽核組長' : DISPATCH_TEAM_MEMBER_ROLE_LABELS[role];
}
/**
 * case_audit_team.role 落庫標籤 → 派案確認角色（「稽核組長」與「主導稽核員」皆視為 lead）。
 * 無法辨識的標籤回 undefined，由呼叫端決定預設。
 */
export function dispatchTeamRoleFromLabel(label) {
    if (!label)
        return undefined;
    if (label === '稽核組長' || label === '主導稽核員')
        return 'lead';
    return Object.keys(DISPATCH_TEAM_MEMBER_ROLE_LABELS).find((r) => DISPATCH_TEAM_MEMBER_ROLE_LABELS[r] === label);
}
/**
 * 稽核員「接收案件」派案確認（序63.1 / 序64 預計日期遷移）。
 *
 * 相容既有僅傳 plannedAuditMonth 的舊呼叫：所有新增欄位皆 optional。
 * 語義：
 * - plannedAuditMonth 保留兼容（若傳 plannedAuditDate，服務端會同步月份）。
 * - plannedAuditDate：預計稽核日期（序64 由開案準備遷移至此，接收案件時填）。
 * - totalPersonDays / fieldDays / prepReportDays：稽核人天數（字串以容納半天，如 "1.5"）。
 * - signatureFileId：稽核員確認簽名（已上傳 files 表，scope=signature）。
 * - teamMembers：選定的稽核小組（user id + 角色 主導稽核員/稽核員/實習稽核員/技術專家/觀察員/驗證助理）；
 *   可含本案原小組以外、於派案確認時新「添加」的成員（序18），服務端以 (caseId,userId) upsert。
 */
export const confirmAuditTaskSchema = z.object({
    /** 計畫稽核月份，格式 YYYY-MM（保留兼容；未傳但傳了 plannedAuditDate 時由服務端補算） */
    plannedAuditMonth: z
        .string()
        .regex(/^\d{4}-\d{2}$/, '格式須為 YYYY-MM')
        .optional(),
    /** 預計稽核日期，格式 YYYY-MM-DD（序64 遷移至派案確認） */
    plannedAuditDate: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, '格式須為 YYYY-MM-DD')
        .optional(),
    /** 稽核總人天數（字串，支援半天，如 "1.5"；範圍 0–999.9） */
    totalPersonDays: z
        .string()
        .regex(/^\d{1,3}(\.\d)?$/, '格式須為數字（最多一位小數）')
        .optional(),
    /** 其中現場稽核人天數 */
    fieldDays: z
        .string()
        .regex(/^\d{1,3}(\.\d)?$/, '格式須為數字（最多一位小數）')
        .optional(),
    /** 其中稽核準備及報告撰寫人天數 */
    prepReportDays: z
        .string()
        .regex(/^\d{1,3}(\.\d)?$/, '格式須為數字（最多一位小數）')
        .optional(),
    /** 稽核員確認簽名文件 ID（已上傳 files 表） */
    signatureFileId: z.number().int().positive().optional(),
    /** 選定的稽核小組成員（每人一個角色；空陣列 / 不傳則不動小組成員角色） */
    teamMembers: z
        .array(z.object({
        /** 成員用戶 ID */
        userId: z.number().int().positive(),
        /** 在案件中的角色 */
        role: z.enum(DISPATCH_TEAM_MEMBER_ROLES),
    }))
        .max(50)
        .optional(),
});
/**
 * 修改稽核人天數（2026-09-15 回報序28）：原於派案確認時填寫，移至「稽核計畫」階段，
 * 可隨時修改。字串以容納半天（如 "1.5"）；傳 null 表示清空。
 */
export const updateAuditPersonDaysSchema = z.object({
    totalPersonDays: z
        .string()
        .regex(/^\d{1,3}(\.\d)?$/, '格式須為數字（最多一位小數）')
        .nullable(),
    fieldDays: z
        .string()
        .regex(/^\d{1,3}(\.\d)?$/, '格式須為數字（最多一位小數）')
        .nullable(),
    prepReportDays: z
        .string()
        .regex(/^\d{1,3}(\.\d)?$/, '格式須為數字（最多一位小數）')
        .nullable(),
});
/**
 * 稽核員「不接收案件」（序63.2）。
 * 必填【驗證組備註】說明原因，提交後任務標記 dispatch_response='rejected' 並通知管理組。
 */
export const rejectDispatchSchema = z.object({
    /** 不接收原因（驗證組備註，必填，至少 5 字） */
    note: z.string().trim().min(5, '請填寫不接收原因（至少 5 字）').max(2000),
});
/**
 * 稽核任務列表查詢參數。
 * - mine: '1' 表示僅返回當前使用者作為 assignee 的任務
 * - status: 過濾任務狀態（中文 enum）
 * - caseId: 過濾關聯案件
 * - assigneeId: 按稽核員過濾（管理組「按人員過濾」用；行級受限者以 mine 口徑為準）
 * - page/pageSize: 分頁
 */
export const auditTaskListQuerySchema = z.object({
    mine: z.enum(['0', '1']).optional(),
    status: z.enum(AUDIT_TASK_STATUSES).optional(),
    caseId: z.coerce.number().int().positive().optional(),
    assigneeId: z.coerce.number().int().positive().optional(),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(200).default(50),
});
/**
 * 從既存案件批量補建稽核任務（手動補救入口）。
 * 主路徑是繳費確認自動觸發；此端點允許 admin/manager 對未自動建任務的「已繳費」案件補建。
 */
export const createAuditTasksFromCaseSchema = z.object({
    caseId: z.number().int().positive(),
});
/**
 * 任務詳情中表單實例的概要資訊。
 * 用於 PhasePlanning 的 FormSelector 與 PhaseField 的查核表清單。
 */
export const taskFormInstanceSummarySchema = z.object({
    id: z.number(),
    templateId: z.number(),
    templateCode: z.string(),
    templateName: z.string(),
    status: z.enum(['draft', 'submitted', 'locked']),
    updatedAt: z.string(),
});
/**
 * 批量綁定/解綁查核表單。
 * 後端執行 diff：
 *  - templateIds 新增的 → 創建 form_instance（owner=audit_task, status=draft）
 *  - templateIds 移除的 → 軟刪對應 form_instance（僅限 status=draft）
 */
export const taskFormBindingSchema = z.object({
    templateIds: z.array(z.number().int().positive()).max(200),
});
/**
 * 檢驗豁免標記請求（PATCH /api/cases/:id/lab-waiver）。
 * waived=true 標記（reason 可選）；waived=false 撤銷（reason 忽略）。
 */
export const labWaiverSchema = z.object({
    waived: z.boolean(),
    reason: z.string().max(500).optional(),
});
/**
 * 稽核任務詳情響應。前端流程化視圖（Steps + Phase Tab）的數據來源。
 */
export const auditTaskDetailSchema = z.object({
    id: z.number(),
    caseId: z.number(),
    caseCode: z.string().nullable(),
    caseScheme: z.enum(VERIFICATION_SCHEMES).nullable(),
    caseVerificationType: z.string().nullable(),
    /**
     * 案件驗證類別（cases.verification_categories，如 ['有機作物']；未填為 null）。
     * 2026-09-25 回報序38：查核表單清單依此過濾「適用驗證類別」。舊客戶端/離線包可能缺此欄，故 optional。
     */
    caseVerificationCategories: z.array(z.string()).nullable().optional(),
    /** 推導出的任務類型，用於匹配表單模板適用範圍 */
    inferredTaskType: z.enum(AUDIT_TASK_TYPES).nullable(),
    clientId: z.number().nullable(),
    clientName: z.string().nullable(),
    clientCode: z.string().nullable(),
    /** 案件主導稽核員（cases.assignedTeamLeadId；前端據此判斷「定稿送審」權限） */
    caseAssignedTeamLeadId: z.number().nullable(),
    assigneeId: z.number(),
    assigneeName: z.string().nullable(),
    isTeamLead: z.boolean(),
    status: z.enum(AUDIT_TASK_STATUSES),
    /** 當前所處階段（前期由前端/服務端依照 status + check-in/out + 表單狀態推斷） */
    phase: z.enum(AUDIT_TASK_PHASES),
    /**
     * 稽核計劃編號（客戶 PPT S47，格式 PLAN-YYYY-NNNNNN）。
     * 派案 bootstrap 建任務時整案一號、同組任務共用；舊任務（欄位新增前建立）為 null。
     */
    planCode: z.string().nullable(),
    /** 案件稽核完成期限（cases.audit_deadline，ISO 字串；管理組派案時指定，未指定為 null） */
    caseAuditDeadline: z.string().nullable(),
    /**
     * 檢驗豁免標記（cases.lab_waived_*）：本案無需檢驗時由管理組/主導稽核員標記，
     * 「檢驗/資材」階段視為已跳過；null = 未標記。
     */
    labWaivedAt: z.string().nullable(),
    /** 檢驗豁免標記人姓名（join users；未標記為 null） */
    labWaivedByName: z.string().nullable(),
    /** 檢驗豁免原因（未填為 null） */
    labWaivedReason: z.string().nullable(),
    plannedAuditMonth: z.string().nullable(),
    /** 預計稽核日期（YYYY-MM-DD 快照；序64 由派案確認填入，未填為 null） */
    plannedAuditDate: z.string().nullable(),
    /** 稽核總人天數（字串，如 "1.5"；序63 派案確認填，未填為 null） */
    totalPersonDays: z.string().nullable(),
    /** 其中現場稽核人天數（序63；未填為 null） */
    fieldDays: z.string().nullable(),
    /** 其中稽核準備及報告撰寫人天數（序63；未填為 null） */
    prepReportDays: z.string().nullable(),
    /**
     * 稽核排程計劃（序67/68，jsonb）：{ days: [{date,startTime,endTime,activity,site,participants}],
     * sampling: {plannedCount,testItems,samplingNote} }；未填為 null。
     */
    scheduleJson: z.unknown().nullable().optional(),
    /** 派案回覆（序63）：'accepted' | 'rejected' | null（尚未回覆） */
    dispatchResponse: z.enum(['accepted', 'rejected']).nullable(),
    /** 不接收案件時的驗證組備註原因（序63.2；未填為 null） */
    dispatchResponseNote: z.string().nullable(),
    arrivedAt: z.string().nullable(),
    leftAt: z.string().nullable(),
    gpsLat: z.number().nullable(),
    gpsLng: z.number().nullable(),
    /** 同案件下其他成員概要（從 case_audit_team join） */
    teammates: z.array(z.object({
        userId: z.number(),
        userName: z.string().nullable(),
        role: z.string(),
        confirmedAt: z.string().nullable(),
    })),
    /** 任務綁定的表單實例概要 */
    forms: z.array(taskFormInstanceSummarySchema),
    createdAt: z.string(),
    updatedAt: z.string(),
});
/**
 * 現場 check-in（到達打卡）
 */
export const checkInSchema = z.object({
    /** GPS 緯度；瀏覽器定位失敗時允許不記錄位置 */
    gpsLat: z.number().min(-90).max(90).nullish(),
    /** GPS 經度；瀏覽器定位失敗時允許不記錄位置 */
    gpsLng: z.number().min(-180).max(180).nullish(),
    /** 到達時間（ISO 格式，可選，預設伺服器 now()） */
    arrivedAt: z.string().datetime().optional(),
});
/**
 * 現場 check-out（離開打卡）
 */
export const checkOutSchema = z.object({
    /** 離開時間（ISO 格式，可選，預設伺服器 now()） */
    leftAt: z.string().datetime().optional(),
});
// ─── 稽核記錄 ──────────────────────────────────────────────────────────────────
/**
 * 提交稽核記錄（關聯表單實例）
 */
export const createAuditRecordSchema = z.object({
    /** 關聯的稽核任務 ID */
    auditTaskId: z.number().int().positive(),
    /** 關聯的表單實例 ID（已在 form_instances 表中） */
    formInstanceId: z.number().int().positive(),
});
// ─── 稽核發現事項 ──────────────────────────────────────────────────────────────
// NC_SEVERITIES and NcSeverity are defined in enums.ts
/**
 * 開立 NC / 發現事項
 */
export const createAuditFindingSchema = z.object({
    /** 客戶端離線 outbox 幂等鍵；同一 key 重試時後端返回既有記錄 */
    clientMutationId: z.string().uuid().optional(),
    /** 嚴重性 */
    severity: z.enum(['重大', '一般', '觀察事項', '改善建議']),
    /** 標準條款引用（可選） */
    clauseRef: z.string().max(100).optional(),
    /** 描述（必填） */
    description: z.string().min(1).max(5000),
    /** 是否需要改正（預設 true） */
    correctionRequired: z.boolean().default(true),
    /** 改正期限（ISO 格式） */
    correctionDueDate: z.string().datetime().optional(),
});
/**
 * 編輯既有 NC / 發現事項（前端「NC 紀錄」tab 的編輯按鈕用）。
 *
 * 可編輯欄位：severity, clauseRef, description, correctionRequired,
 *             correctionDueDate, clientResponse。
 * 不可改：code（NC 編號）、caseId、createdAt、createdBy。
 *
 * 所有欄位皆 optional，允許 partial update；以 null 顯式清除可空欄位。
 */
export const updateAuditFindingSchema = z.object({
    /** 嚴重性 */
    severity: z.enum(['重大', '一般', '觀察事項', '改善建議']).optional(),
    /** 標準條款引用（傳 null 清除） */
    clauseRef: z.string().max(100).nullable().optional(),
    /** 描述 */
    description: z.string().min(1).max(5000).optional(),
    /** 是否需要改正 */
    correctionRequired: z.boolean().optional(),
    /** 改正期限（ISO 格式；傳 null 清除） */
    correctionDueDate: z.string().datetime().nullable().optional(),
    /** 客戶回應內容（傳 null 清除） */
    clientResponse: z.string().max(5000).nullable().optional(),
});
/**
 * 客戶簽名上傳
 */
export const signFindingSchema = z.object({
    /** 簽名文件 ID（已上傳至 files 表） */
    signatureFileId: z.number().int().positive(),
});
/**
 * 更新 NC 客戶回應
 */
export const updateFindingResponseSchema = z.object({
    /** 客戶回應內容 */
    clientResponse: z.string().min(1).max(5000),
});
// ─── 採樣 ──────────────────────────────────────────────────────────────────────
// SAMPLING_TYPES and SamplingType are defined in enums.ts; imported via @zhcb/shared top-level export
/**
 * 採樣登記（「採樣送檢」第一步：採樣單資訊 + 可選採樣位置手繪示意圖）。
 *
 * 正式表單（採樣單 TOP=ZHSR03-6/-7、TAP=ZHSR03-2/-3；送檢單 TOP=ZHSR03-5、
 * TAP=ZHSR03-1）的實例關聯（samplingFormInstanceId / submissionFormInstanceId）
 * 本期僅在 DB 留列，端點暫不接受，待客戶提供表樣後補齊。
 */
export const createSampleSchema = z.object({
    /** 客戶端離線 outbox 幂等鍵；同一 key 重試時後端返回既有記錄 */
    clientMutationId: z.string().uuid().optional(),
    /** 採樣類型 */
    samplingType: z.enum(['現場採樣', '年度後市場採樣', '質譜快檢']),
    /**
     * 樣品分類（序72：採樣單樣品分類）：
     *  product（產品）/ water_soil（水、土壤）/ honey（蜂蜜產品）。
     * 後端據此 + case scheme 選採樣單模板（見 @zhcb/shared resolveSamplingTemplate）
     * 並自動建立採樣單 form_instance；未帶時預設 product。
     */
    sampleCategory: z.enum(['product', 'water_soil', 'honey']).optional(),
    /** 樣品描述 */
    sampleDescription: z.string().max(2000).optional(),
    /** 採樣時間（ISO 格式） */
    collectedAt: z.string().datetime().optional(),
    /** 送驗實驗室 ID（必填，需來自啟用且未軟刪的 laboratories；後端會做查詢驗證） */
    labId: z.number().int().positive(),
    /** 送驗時間（ISO 格式） */
    sentAt: z.string().datetime().optional(),
    /** 採樣位置手繪示意圖文件 ID（前端畫板輸出 PNG 上傳 files 後回填，可選） */
    sketchFileId: z.number().int().positive().optional(),
});
/**
 * 送檢登記（序72「採樣送檢拆兩步」的第二步：對已採樣的 sample 建送檢單）。
 *
 * 後端依 case scheme 選送檢單模板（TOP=D-ZHSR03-5、TAP=TAP-ZHSR03-1；
 * 見 @zhcb/shared resolveSubmissionTemplate）建 submissionFormInstance
 * 並寫 sentAt（未帶則以當下時間為送檢時間）。
 */
export const createSampleSubmissionSchema = z.object({
    /** 送驗時間（ISO 格式；未帶時後端以當下時間寫入） */
    sentAt: z.string().datetime().optional(),
});
/**
 * 編輯既有採樣記錄（前端「採樣」tab 的編輯按鈕用）。
 *
 * 可編輯欄位：samplingType, sampleDescription, collectedAt, labName,
 *             reportFileId, result, reportReceivedAt, sketchFileId。
 * 不可改：code（採樣編號 LAB-…）、caseId、createdAt、createdBy。
 *
 * 所有欄位皆 optional，允許 partial update；以 null 顯式清除可空欄位
 * （samplingType 為 NOT NULL 不允許 null；sampleDescription/collectedAt/
 * labName/reportFileId/result/reportReceivedAt 皆 nullable）。
 *
 * 註：result 為 varchar(20) 自由字串（語意上 'pass' / 'fail' / 'pending'，
 * 但 schema 層暫不限制 enum，與 DB 設計一致）。
 */
export const updateSampleSchema = z.object({
    /** 採樣類型 */
    samplingType: z.enum(['現場採樣', '年度後市場採樣', '質譜快檢']).optional(),
    /** 樣品描述（傳 null 清除） */
    sampleDescription: z.string().max(2000).nullable().optional(),
    /** 採樣時間（ISO 格式；傳 null 清除） */
    collectedAt: z.string().datetime().nullable().optional(),
    /** 送驗實驗室 ID（可選；傳了即重新關聯並刷新 labName 快照） */
    labId: z.number().int().positive().optional(),
    /** 實驗室報告文件 ID（傳 null 清除；對應已上傳至 files 表的記錄） */
    reportFileId: z.number().int().positive().nullable().optional(),
    /** 檢測結果（自由字串，慣用 'pass' / 'fail' / 'pending'；傳 null 清除） */
    result: z.string().max(20).nullable().optional(),
    /** 收到報告時間（ISO 格式；傳 null 清除） */
    reportReceivedAt: z.string().datetime().nullable().optional(),
    /** 採樣位置手繪示意圖文件 ID（重繪後回填新 fileId；傳 null 清除） */
    sketchFileId: z.number().int().positive().nullable().optional(),
});
// ─── 實驗室報告 ────────────────────────────────────────────────────────────────
/**
 * 上傳實驗室報告
 */
export const createLabReportSchema = z.object({
    /** 關聯樣品 ID */
    sampleId: z.number().int().positive(),
    /** 報告文件 ID */
    fileId: z.number().int().positive().optional(),
    /** 實驗室報告編號 */
    reportNumber: z.string().max(100).optional(),
    /** 檢測項目（陣列，結構自由） */
    testItems: z.array(z.unknown()).optional(),
    /** 是否通過 */
    passOrFail: z.boolean().optional(),
    /** 備注 */
    notes: z.string().max(5000).optional(),
});
/**
 * 更新（替換）實驗室報告（序13：誤傳/重複上傳補救）。
 *
 * 所有欄位皆可選，僅更新提供的欄位（PATCH 語義）；
 * 換檔（fileId）時後端會回寫 samples.reportFileId。
 */
export const updateLabReportSchema = z.object({
    /** 報告文件 ID（換檔時傳新檔） */
    fileId: z.number().int().positive().nullable().optional(),
    /** 實驗室報告編號 */
    reportNumber: z.string().max(100).nullable().optional(),
    /** 檢測項目（陣列，結構自由） */
    testItems: z.array(z.unknown()).nullable().optional(),
    /** 是否通過 */
    passOrFail: z.boolean().nullable().optional(),
    /** 備注 */
    notes: z.string().max(5000).nullable().optional(),
});
/** 判讀方式枚舉（序13）：auto=系統自動判讀 / manual=人工判讀 */
export const LAB_REPORT_JUDGMENT_MODES = ['auto', 'manual'];
/**
 * 判讀實驗室報告（序13：②系統判讀 + ③人工判讀回退）。
 *
 * - mode='auto'：系統輕判讀（目前為規則式佔位，見 service TODO）。
 * - mode='manual'：人工判讀，必須明確給出 passOrFail 與判定依據。
 */
export const judgeLabReportSchema = z.object({
    /** 判讀方式 */
    mode: z.enum(LAB_REPORT_JUDGMENT_MODES),
    /** 判定合格與否（人工判讀必填；系統判讀可空＝無法判定回退人工） */
    passOrFail: z.boolean().optional(),
    /** 判定依據（人工判讀時建議填寫，引用對應表單/標準） */
    judgmentBasis: z.string().max(5000).optional(),
    /**
     * 殘留農藥安全容許量標準版本號（序74：ZHSR02-37/ZHSR02-25 判定單「序號11」欄，
     * 由稽核員輸入，如「農藥殘留容許量標準 112.06.15 版」）。
     */
    standardVersion: z.string().max(200).optional(),
    /** 判定者簽名檔 file id（序74：判定結果＋簽字＝通過/不通過；可導入員工簽名檔） */
    judgeSignatureFileId: z.number().int().positive().optional(),
});
// ─── 有機資材審查 ──────────────────────────────────────────────────────────────
/** 審查結果枚舉 */
export const REVIEW_RESULTS = ['approved', 'rejected', 'conditional'];
/**
 * 有機資材審查
 */
export const createOrganicMaterialReviewSchema = z.object({
    /** 資材 ID */
    materialId: z.number().int().positive(),
    /** 審查結果 */
    reviewResult: z.enum(REVIEW_RESULTS),
    /** 審查者姓名（可選） */
    reviewer: z.string().max(100).optional(),
    /** 備注 */
    notes: z.string().max(2000).optional(),
});
// ─── 風險評估 ──────────────────────────────────────────────────────────────────
/**
 * 風險評估
 */
export const createRiskAssessmentSchema = z.object({
    /** 風險分數 0-100 */
    riskScore: z.number().int().min(0).max(100),
    /** 風險因素陣列 */
    factors: z
        .array(z.object({
        factor: z.string(),
        weight: z.number().optional(),
        score: z.number().optional(),
    }))
        .optional(),
    /** 授證建議 */
    recommendation: z.string().max(100).optional(),
    /** 補充說明 */
    notes: z.string().max(5000).optional(),
});
// ─── 報告樣式模板 ──────────────────────────────────────────────────────────────
/**
 * 建立報告樣式模板
 */
export const createReportStyleSchema = z.object({
    /** 模板名稱 */
    name: z.string().min(1).max(100),
    /**
     * 描述。序43（2026-09-25）：前端未填時送 null（清空語義），原 schema 只接受 undefined
     * 導致建立/儲存一律 400，故三個選填欄位皆放寬為 nullable。
     */
    description: z.string().max(1000).nullable().optional(),
    /** 是否設為預設 */
    isDefault: z.boolean().default(false),
    /** 範例報告 ID 陣列（null / 省略 = 不引用範例報告） */
    exampleReportIds: z.array(z.number().int().positive()).nullable().optional(),
    /** AI 提示詞指令（null / 省略 = 不額外指定） */
    promptDirectives: z.string().max(10000).nullable().optional(),
});
/**
 * 更新報告樣式模板（所有欄位可選）
 */
export const updateReportStyleSchema = createReportStyleSchema.partial();
// ─── AI 報告生成 ───────────────────────────────────────────────────────────────
/**
 * 提交 AI 報告生成 Job
 */
export const submitAuditReportDraftSchema = z.object({
    /** 案件 ID */
    caseId: z.number().int().positive(),
    /** 報告樣式模板 ID（可選） */
    reportStyleTemplateId: z.number().int().positive().optional(),
});
/**
 * 提交報告版本進入評審。
 * 由稽核員執行：把指定 version 標記 submitted，audit_reports.status 轉為「評審中」。
 */
/** 驗證建議選項（序44：定稿時由主導稽核員勾選） */
export const REPORT_RECOMMENDATIONS = ['issue', 'partial', 'no_issue'];
/** 驗證建議中文標籤（前後端共用展示） */
export const REPORT_RECOMMENDATION_LABELS = {
    issue: '建議發放驗證證書',
    partial: '建議發放部分驗證範圍驗證證書',
    no_issue: '不建議發放驗證證書',
};
export const submitAuditReportSchema = z
    .object({
    /** 要提交的版本 ID（通常是最新 AI 草稿或人工編輯版本） */
    versionId: z.number().int().positive(),
    /**
     * 驗證建議（序44.3：AI 生成【驗證建議】位置移到定稿處，由主導稽核員勾選）：
     * issue=建議發放 / partial=建議發放部分範圍（須說明）/ no_issue=不建議發放（須說明）。
     * optional 兼容舊呼叫；前端定稿對話框必選。
     */
    recommendation: z.enum(REPORT_RECOMMENDATIONS).optional(),
    /** 驗證建議說明（partial / no_issue 必填，issue 選填） */
    recommendationNote: z.string().max(2000).optional(),
    /** 主導稽核員簽名檔 file id（序44.4：定稿簽名；可導入員工簽名檔） */
    leadSignatureFileId: z.number().int().positive().optional(),
})
    .refine((v) => !v.recommendation || v.recommendation === 'issue' || !!v.recommendationNote?.trim(), { message: '選擇部分範圍發放或不建議發放時，應說明原因' });
/**
 * 核准 / 退回稽核報告。
 * 由管理員或經理執行：寫入 reviewedBy/reviewedAt/reviewNotes，
 * 通過則 audit_reports.status='終稿' 且 currentVersionId 指向該版本；
 * 退回則 audit_reports.status='草稿'，提交人需重新編輯後再提交。
 */
export const reviewAuditReportSchema = z.object({
    /** 要評審的版本 ID */
    versionId: z.number().int().positive(),
    /** 評審決定 */
    decision: z.enum(['approve', 'reject']),
    /** 評審備註（退回時必填） */
    reviewNotes: z.string().max(2000).optional(),
});
// ─── 批量同步 ──────────────────────────────────────────────────────────────────
/**
 * 批量同步操作項
 */
export const syncOperationSchema = z.object({
    /** 客戶端本地 ID（用於響應對照） */
    localId: z.string().min(1).max(64),
    /** HTTP 方法 */
    method: z.enum(['POST', 'PUT', 'PATCH', 'DELETE']),
    /** 目標 URL（必須以 /api/ 開頭） */
    url: z.string().startsWith('/api/'),
    /** 請求體 */
    body: z.unknown().optional(),
    /** 客戶端版本號（用於衝突檢測） */
    clientVersion: z.number().int().optional(),
});
/**
 * 批量同步請求體
 */
export const batchSyncSchema = z.object({
    operations: z.array(syncOperationSchema).min(1).max(100),
});
